"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { baseUrl, localUrl } from "../../config/baseUrl";
import axios from "axios";
import toastify from "../../config/toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faImage,
  faSave,
  faUpload,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";

export default function NewsEdit() {
  const { id } = useParams();
  const [formval, setFormval] = useState({
    title: "",
    description: "",
    status: "",
    image: null,
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(
            // `${localUrl}/admin/news/${id}`,
            `${baseUrl}/admin/news/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (res?.data?.status) {
          const news = res.data.data || {};
          setFormval({
            title: news.title,
            description: news.description,
            status: news.status,
            image: null,
          });
          if (news.first_image) {
            setPreviewUrl(`${baseUrl}/${news.first_image}`);
            // setPreviewUrl(`${localUrl}/${news.first_image}`);
          }
        } else {
          toastify.error("Failed to fetch news data");
        }
      } catch (error) {
        console.error("Error fetching news:", error);
        toastify.error("Server error while fetching news");
      }
    };

    if (id) fetchNews();
  }, [id]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setFormval((prevVal) => ({
      ...prevVal,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormval((prevVal) => ({
      ...prevVal,
      description: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formval.title) return toastify.error("Title is required");
    if (!formval.description) return toastify.error("Description is required");

    setLoading(true);

    try {
    //   const plainText = formval.description.replace(/<[^>]+>/g, "");

      const formData = new FormData();
      formData.append("title", formval.title);
      formData.append("description", formval.description);
      formData.append("status", formval.status);
      if (selectedFile) formData.append("first_image", selectedFile);

      const response = await axios.put(
        // `${localUrl}/admin/news/update/${id}`,
        `${baseUrl}/admin/news/update/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data.status) {
        toastify.success(response?.data.message);
        navigate("/news-list");
      } else {
        toastify.error(response?.data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      toastify.error("Failed to update news");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="main-sec">
      <div className="container-fluid">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <div className="dashboard-title">
              <h4 className="dash-head">
                <FontAwesomeIcon icon={faImage} className="me-2" />
                Edit News
              </h4>
            </div>
            <div className="custom-bredcump">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/news-list">News List</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Edit News
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="col-lg-6 d-flex justify-content-end">
            <Link to="/news-list" className="btn btn-info text-white">
              <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
              Back
            </Link>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div className="cards edit-usr">
              <form onSubmit={handleSubmit}>
                <div className="form-section">
                  <h5 className="section-title">News Details</h5>
                  <div className="row">
                    <div className="col-lg-12 mb-4">
                      <label className="form-label">
                        Title <sup className="text-danger">*</sup>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        placeholder="Enter news title"
                        onChange={changeHandler}
                        value={formval.title}
                      />
                    </div>
                    <div className="col-lg-12 mb-4">
                      <label className="form-label">
                        Description <sup className="text-danger">*</sup>
                      </label>
                      <ReactQuill
                        value={formval.description}
                        onChange={handleQuillChange}
                      />
                      <small className="text-muted">
                        Content will be saved as plain text
                      </small>
                    </div>
                    <div className="col-lg-12 mb-4">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        name="status"
                        value={formval.status}
                        onChange={changeHandler}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h5 className="section-title">Image</h5>
                  <div className="file-upload-container">
                    <div
                      className={`file-upload-area ${
                        isDragging ? "dragging" : ""
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      {!selectedFile && !previewUrl ? (
                        <>
                          <div className="file-upload-icon">
                            <FontAwesomeIcon icon={faUpload} />
                          </div>
                          <div className="file-upload-text">
                            Drag & Drop your image here
                          </div>
                          <div className="file-upload-subtext">
                            or click to browse files
                          </div>
                          <input
                            type="file"
                            className="file-upload-input"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </>
                      ) : (
                        <div className="file-preview-container">
                          <img
                            src={previewUrl || "/appicon.png"}
                            alt="Preview"
                            className="file-preview"
                          />
                          <button
                            type="button"
                            className="file-remove-btn"
                            onClick={removeFile}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                          {selectedFile && (
                            <div className="file-info">
                              {selectedFile.name} (
                              {Math.round(selectedFile.size / 1024)} KB)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-12 text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-for-add text-white"
                    disabled={loading}
                  >
                    {loading && <div className="loading-spinner"></div>}
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {loading ? "Updating..." : "Update News"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
